/*
 * Copyright (C) 2024 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and
 * should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly
 * prohibited.
 */
import {
  DataPath,
  MAC_ADDRESS_NETWORKS,
  Modem,
  SAT_IP_NETWORKS,
  TerminalType,
  TPK_NETWORKS,
  SERIAL_NUMBER_NETWORKS,
} from '../../../shared/entities/provisionedTerminal';
import { AccordionDetails, AccordionSummary, Box, IconButton, Typography } from '@mui/material';
import { BeamInput } from '../../common/formik/BeamInput';
import { useMemo, useState } from 'react';
import { BeamSelect } from '../../common/formik/BeamSelect';
import { selectAntennaTypeOptions, selectModemTypeOptions } from '../../../store';
import { shallowEqual, useSelector } from 'react-redux';
import { CDP_ASSET_ID_PATTERN, FTI_PATTERN, MAC_ADDR_PATTERN } from '../../../utils/RegexLib';
import { Antenna } from '../../../shared/entities/options/antennas';
import { BeamAccordion } from '../../common/beam/BeamAccordion';
import { Delete, Warning } from '@mui/icons-material';
import appTheme from '../../common/theme/appTheme.json';
import ModalDialog from '../../common/dialogs/ModalDialog';

const checkModemRequirements = (
  modem: Partial<Modem>,
  setFieldValue: Function,
  datapathIdx: number,
  modemIdx: number,
) => {
  if (modem.networks === undefined)
    return {
      satIpRequired: false,
      macAddressRequired: false,
      tpkRequired: false,
      cdpAssetIdRequired: false,
      serialNumberRequired: false,
    };

  // Figure out if we need to ask the user for a satellite IP, MAC address, TPK, or Serial Number & CDP Asset ID based
  // on the networks and/or modem type they currently have selected.
  const satIpRequired = modem.networks.some(network => SAT_IP_NETWORKS.includes(network));
  const macAddressRequired = modem.networks.some(network => MAC_ADDRESS_NETWORKS.includes(network));
  const tpkRequired = modem.networks.some(network => TPK_NETWORKS.includes(network)) && modem.type !== 'Axiom Air';
  const serialNumberRequired =
    modem.networks.some(network => SERIAL_NUMBER_NETWORKS.includes(network)) && modem.type === 'Axiom Air';
  const cdpAssetIdRequired =
    modem.networks.some(network => SERIAL_NUMBER_NETWORKS.includes(network)) && modem.type === 'Axiom Air';

  // Null out the sat IP and mac address fields if they aren't required. Otherwise, if a user had already
  // entered a value for either, it would linger in the background and be submitted to the backend.
  if (!satIpRequired && modem.satIp !== null) {
    setFieldValue(`datapaths[${datapathIdx}].modems[${modemIdx}].satIp`, null);
  }
  if (!macAddressRequired && modem.macAddress !== null) {
    setFieldValue(`datapaths[${datapathIdx}].modems[${modemIdx}].macAddress`, null);
  }
  if (!tpkRequired && modem.tpk !== null) {
    setFieldValue(`datapaths[${datapathIdx}].modems[${modemIdx}].tpk`, null);
  }
  if (!serialNumberRequired && modem.serialNumber !== null) {
    setFieldValue(`datapaths[${datapathIdx}].modems[${modemIdx}].serialNumber`, null);
  }
  if (!cdpAssetIdRequired && modem.cdpAssetId !== null) {
    setFieldValue(`datapaths[${datapathIdx}].modems[${modemIdx}].cdpAssetId`, null);
  }

  return { satIpRequired, macAddressRequired, tpkRequired, cdpAssetIdRequired, serialNumberRequired };
};

const ModemBuilder = ({
  modem,
  modemIdx,
  datapath,
  datapathIdx,
  deleteEnabled,
  terminalType,
  setFieldValue,
  onDelete,
}: {
  modem: Modem;
  modemIdx: number;
  datapath: DataPath;
  datapathIdx: number;
  deleteEnabled: boolean;
  terminalType: TerminalType;
  setFieldValue: Function;
  onDelete: (datapathIdx: number) => void;
}) => {
  const { satIpRequired, macAddressRequired, tpkRequired, cdpAssetIdRequired, serialNumberRequired } = useMemo(() => {
    return checkModemRequirements(modem, setFieldValue, datapathIdx, modemIdx);
  }, [modem, setFieldValue, datapathIdx, modemIdx]);

  const antennaOptions = useSelector(selectAntennaTypeOptions);
  const selectedAntenna: Antenna | undefined = antennaOptions?.find(
    opt => opt.name === datapath.antenna || opt.displayName === datapath.antenna,
  );
  const supportedNetworksForSelectedAntenna = selectedAntenna?.supportedNetworks ?? [];

  const modemTypes = useSelector(selectModemTypeOptions, shallowEqual);

  const displayModemTypeInput = useMemo(() => {
    return terminalType === 'HAN';
  }, [terminalType]);

  // State to track if the delete modal should be shown
  const [showDeleteModemModal, setShowDeleteModemModal] = useState(false);

  // Don't display modem configuration if the antenna hasn't been selected yet
  if (!selectedAntenna) return <></>;

  return (
    <>
      <BeamAccordion
        key={datapath.label}
        defaultExpanded
        sx={{
          border: 'none !important',
          borderRadius: '0px !important',
          boxShadow: 'none !important',
          '&:before': {
            display: 'none !important', // This removes the default border line when accordion is collapsed
          },
        }}
      >
        <AccordionSummary sx={{ padding: '0px' }}>
          <Typography
            sx={{
              fontFamily: 'Source Sans Pro',
              fontStyle: 'normal',
              fontWeight: 600,
              fontSize: '14px',
              lineHeight: '20px',
              color: appTheme.mptFormLabelText,
            }}
          >
            {`Modem #${modemIdx + 1}`}
          </Typography>
          {deleteEnabled && (
            <IconButton
              data-testid={`delete-modem-${modemIdx}`}
              onClick={event => {
                event.stopPropagation();
                setShowDeleteModemModal(true);
              }}
              sx={{ padding: 0, marginLeft: 'auto' }}
            >
              <Delete sx={{ color: appTheme.primaryTextColor, fontSize: 24 }} />
            </IconButton>
          )}
        </AccordionSummary>

        <AccordionDetails style={{ display: 'flex', flexDirection: 'column', gap: '8px', padding: '0px' }}>
          {displayModemTypeInput && (
            <BeamSelect
              sx={{ pl: 2 }}
              name={`datapaths[${datapathIdx}].modems[${modemIdx}].type`}
              label={'Modem Type'}
              options={modemTypes ?? []}
              placeholder={'Select modem type'}
              required={true}
            />
          )}

          <BeamSelect
            sx={{ pl: 2 }}
            name={`datapaths[${datapathIdx}].modems[${modemIdx}].networks`}
            label={'Network'}
            options={supportedNetworksForSelectedAntenna}
            placeholder={'Select a network'}
            required={true}
            onChange={(_e, value) => {
              // The DTO expects an array, but the underlying MUI component
              // only returns a string representing the selected option.
              // This is a workaround to ensure the value set in the DTO is
              // always an array.
              return [value];
            }}
            getOptionLabel={option => {
              // This is coupled to the logic and comment in onChange, re: DTOs.
              let label: string;
              if (Array.isArray(option)) {
                label = option[0] ?? '';
              } else {
                label = option ?? '';
              }
              if (label === 'Arclight-1') return 'ArcLight (VGNet/HCX)';
              return label;
            }}
            isOptionEqualToValue={(option, value) => {
              // This is coupled to the logic and comment in onChange, re: DTOs.
              if (Array.isArray(option)) {
                option = option[0];
              }
              if (Array.isArray(value)) {
                value = value[0];
              }
              return option === value;
            }}
          />

          {satIpRequired && (
            <BeamInput
              sx={{ pl: 4 }}
              name={`datapaths[${datapathIdx}].modems[${modemIdx}].satIp`}
              helpText={'Format: 123.234'}
              label={'Satellite IP'}
              required={true}
              pattern={FTI_PATTERN}
            />
          )}

          {macAddressRequired && (
            <BeamInput
              sx={{ pl: 4 }}
              name={`datapaths[${datapathIdx}].modems[${modemIdx}].macAddress`}
              helpText={'Format: 00:11:22:aa:bb:cc'}
              label={'MAC Address'}
              required={true}
              pattern={MAC_ADDR_PATTERN}
            />
          )}

          {tpkRequired && (
            <BeamInput
              sx={{ pl: 4 }}
              name={`datapaths[${datapathIdx}].modems[${modemIdx}].tpk`}
              helpText={undefined}
              label={'TPK'}
              required={true}
              pattern={undefined}
            />
          )}

          {cdpAssetIdRequired && (
            <BeamInput
              sx={{ pl: 4 }}
              name={`datapaths[${datapathIdx}].modems[${modemIdx}].cdpAssetId`}
              helpText={'This is the SCPC Modem Asset ID defined for the modem at CDP'}
              label={'CDP Asset ID'}
              required={true}
              pattern={CDP_ASSET_ID_PATTERN}
            />
          )}

          {serialNumberRequired && (
            <BeamInput
              sx={{ pl: 4 }}
              name={`datapaths[${datapathIdx}].modems[${modemIdx}].serialNumber`}
              helpText={undefined}
              label={'Serial Number'}
              required={true}
              pattern={undefined}
            />
          )}
        </AccordionDetails>
      </BeamAccordion>
      {deleteEnabled && (
        <ModalDialog
          open={showDeleteModemModal}
          onLeftBtnClicked={() => {
            setShowDeleteModemModal(false);
          }}
          onRightBtnClicked={() => {
            onDelete(modemIdx);
            setShowDeleteModemModal(false);
          }}
          title={
            <Box display={'flex'} gap={'8px'} alignItems={'center'}>
              <Warning htmlColor={'#FFB029'} /> Confirm Modem Deletion
            </Box>
          }
          message={
            <>
              Are you sure you want to delete this modem?
              <br />
              <br />
              This action cannot be undone.
            </>
          }
        />
      )}
    </>
  );
};

export default ModemBuilder;
